import {
  Button,
  IconButton,
  ToggleButton,
  Table,
  TableHeading,
  TableCell,
  Link,
  TextField,
  Icon,
  Skeleton,
  Snackbar,
  Snack,
  Spinner,
} from '@nike/eds';
import * as R from 'ramda';
import { useInfiniteQuery } from '@tanstack/react-query';
import { useInView } from 'react-intersection-observer';
import { useState, useEffect, Fragment } from 'react';
import {
  useNavigate,
  Link as RouterLink,
  useLoaderData,
  useRevalidator,
} from 'react-router-dom';
import { useDebounce } from 'use-debounce';
import dayjs from 'dayjs';
import { useAtom } from 'jotai';
import TriggerConcepts from 'components/RankingRule/TriggerConcepts';
import RankingRuleStatusModal from 'components/RankingRule/RankingRuleStatusModal';
import { QUERY_KEYS } from '../../constants';
import RankingClient from '../../clientAPI/ranking';
import { rankingRuleEditResult } from '../../atoms/ranking';
import { isExistScope } from '../../utils/authHelpers';

const headers = [
  'name',
  'concept trigger',
  'priority',
  'status',
  'start date',
  'end date',
  'modification date',
  'modified by',
];
const RootListLoader = ({ count }: { count: number }) => {
  return (
    <>
      {R.range(1, count).map((c) => (
        <Skeleton height={60} width={'80%'} key={c} className='tw-m-8' />
      ))}
    </>
  );
};
const isValidUUID = (str: string) =>
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-5][0-9a-f]{3}-[089ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(
    str,
  );

export default function Ranking() {
  const navigate = useNavigate();
  const [searchRuleQuery, setSearchRuleQuery] = useState('');
  const [searchRuleQueryVal] = useDebounce(searchRuleQuery, 1000);
  const [isStatusModalOpen, setIsStatusModalOpen] = useState(false);
  const [ruleToUpdateStatus, setRuleToUpdateStatus] =
    useState<RankingRule | null>(null);
  const resetSearch = () => {
    setSearchRuleQuery('');
  };
  const { ref, inView } = useInView({ delay: 1000 });
  const loaderData = useLoaderData() as { access_token: string };
  const revalidator = useRevalidator();
  const [updateResult, setUpdateResult] = useAtom(rankingRuleEditResult);
  const jwt = loaderData.access_token;
  const canWrite = isExistScope('search360:ranking:write', jwt);
  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetching,
    isFetchingNextPage,
    status,
  } = useInfiniteQuery({
    queryKey: [QUERY_KEYS.RANKING_RULE_LIST, searchRuleQueryVal],
    queryFn: async ({ pageParam }) => {
      if (pageParam) {
        return RankingClient.next(pageParam);
      } else {
        const params =
          searchRuleQueryVal === ''
            ? {}
            : isValidUUID(searchRuleQueryVal.trim())
            ? { ruleId: searchRuleQueryVal.trim() }
            : { ruleName: searchRuleQueryVal.trim() };
        return RankingClient.getRules(params);
      }
    },
    getNextPageParam: (lastPage, pages) =>
      lastPage && lastPage.pages.next !== '' ? lastPage.pages.next : undefined,
    staleTime: Infinity,
    onError: (err: { status: number }) =>
      err.status === 401 && revalidator.revalidate(),
  });

  const openStatusModal = (ruleData: RankingRule) => {
    setRuleToUpdateStatus(ruleData);
    setIsStatusModalOpen(true);
  };
  const closeStatusModal = () => {
    setIsStatusModalOpen(false);
  };
  const updateStatusSuccessCallback = () => {
    setUpdateResult((result) => {
      result.resultMsg = 'Rule status updated!';
      result.hasError = false;
    });
  };
  const updateStatusFailureCallback = () => {
    setUpdateResult((result) => {
      result.resultMsg = 'Rule status update failed!';
      result.hasError = true;
    });
  };

  useEffect(() => {
    if (inView && hasNextPage) {
      fetchNextPage();
    }
  }, [inView]);
  return (
    <div>
      <div className='tw-p-3'>
        <div className='tw-mb-4 tw-flex tw-items-center tw-gap-4'>
          <div className='tw-w-1/3'>
            <TextField
              label=''
              id='search-ranking-rule-by-id'
              value={searchRuleQuery}
              beforeSlot={<Icon name='Search' />}
              placeholder='Enter Rule ID or Name'
              onChange={(event) => setSearchRuleQuery(event.target.value)}
            />
          </div>
          <div className='tw-mt-4'>
            <Button onClick={resetSearch}>CLEAR</Button>
          </div>
        </div>
        {isFetching && <RootListLoader count={10} />}
        <Table className='tw-w-full'>
          <thead>
            <tr>
              {headers.map((column) => (
                <TableHeading key={column} className='tw-uppercase'>
                  {column}
                </TableHeading>
              ))}
            </tr>
          </thead>
          <tbody>
            {data &&
              data.pages.map((page, i) => (
                <Fragment
                  key={
                    page.pages.next
                      ? page.pages.next
                      : `ranking_rules_page_1_${i}`
                  }
                >
                  {page.data.map((row, idx) => {
                    return (
                      <tr key={row._id}>
                        <TableCell>
                          <Link
                            as={RouterLink}
                            to={`/ranking/edit-rule/${row._id}`}
                          >
                            {row.name}
                          </Link>
                        </TableCell>
                        <TableCell>
                          <div
                            className='tw-truncate'
                            style={{ maxWidth: '20rem' }}
                          >
                            {row.trigger.queries &&
                              row.trigger.queries.classifications?.map(
                                (clf, idx) => {
                                  return (
                                    <div key={idx} className='tw-truncate'>
                                      <TriggerConcepts
                                        conceptIds={clf.classificationValues}
                                      />
                                    </div>
                                  );
                                },
                              )}
                          </div>
                        </TableCell>
                        <TableCell>{row.priority}</TableCell>
                        <TableCell>
                          {canWrite && (
                            <ToggleButton
                              onClick={() => openStatusModal(row)}
                              active={row.status === 'ACTIVE'}
                              title='status'
                            >
                              {row.status || 'undefined'}
                            </ToggleButton>
                          )}
                          {!canWrite && (
                            <ToggleButton
                              active={row.status === 'ACTIVE'}
                              title='status'
                            >
                              {row.status || 'undefined'}
                            </ToggleButton>
                          )}
                        </TableCell>
                        <TableCell>
                          {dayjs(row.startDate).format('YYYY-M-D HH:mm')}
                        </TableCell>
                        <TableCell>
                          {dayjs(row.endDate).format('YYYY-M-D HH:mm')}
                        </TableCell>
                        <TableCell>
                          {dayjs(row.modificationDate).format('YYYY-M-D HH:mm')}
                        </TableCell>
                        <TableCell>{row.modifiedBy}</TableCell>
                      </tr>
                    );
                  })}
                </Fragment>
              ))}
          </tbody>
        </Table>
        <div className='tw-w-full tw-h-10 tw-mx-4 tw-text-center' ref={ref}>
          {isFetchingNextPage ? (
            <Spinner className='tw-ml-12 tw-my-2' />
          ) : (
            <></>
          )}
        </div>
        <RankingRuleStatusModal
          isOpen={isStatusModalOpen}
          ruleData={ruleToUpdateStatus || ({} as RankingRule)}
          onClose={closeStatusModal}
          successCallback={updateStatusSuccessCallback}
          failureCallback={updateStatusFailureCallback}
        />
      </div>

      {canWrite && (
        <div className='tw-fixed tw-bottom-10 tw-right-20'>
          <IconButton
            icon='Plus'
            label='New'
            onClick={() => navigate('/ranking/create-rule')}
          />
        </div>
      )}
      <Snackbar style={{ left: '90%', top: '90%' }}>
        {updateResult.resultMsg && (
          <Snack
            autoDismissDuration={5000}
            id='ranking-rule-error-notification'
            onDismiss={(id) => {
              setUpdateResult((result) => {
                result.resultMsg = '';
                result.hasError = false;
              });
            }}
            status={updateResult.hasError ? 'error' : 'success'}
          >
            {updateResult.resultMsg}
          </Snack>
        )}
      </Snackbar>
    </div>
  );
}
